import { useFrame, useThree } from "@react-three/fiber";
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  type Camera,
  HalfFloatType,
  type Object3D,
  type PerspectiveCamera,
  type Scene,
  Vector2,
  WebGLRenderTarget,
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import type { Pass } from "three/examples/jsm/postprocessing/Pass.js";
import { SMAAPass } from "three/examples/jsm/postprocessing/SMAAPass.js";
import { useAmbientOcclusionTuning } from "../stores/ambientOcclusionTuningStore";
import {
  AMBIENT_OCCLUSION,
  type AmbientOcclusionTuning,
} from "./ambientOcclusionSettings";
import {
  type AnyProductStoreHook,
  ProductStoreContext,
} from "./ProductStoreContext";
import { ProductViewerBeautyPass } from "./ProductViewerBeautyPass";
import {
  clampAmbientOcclusionResolutionScale,
  ProductViewerGTAOPass,
} from "./ProductViewerGTAOPass";
import { ProductViewerOutputPass } from "./ProductViewerOutputPass";
import { createPostProcessingCameraActivity } from "./postProcessingCameraActivity";
import { isSceneDragActive } from "./productDragScope";

/**
 * Layer for scene helpers that must not take part in ambient occlusion.
 *
 * The AO pass builds its G-buffer with `scene.overrideMaterial`, which replaces
 * every material with an opaque normal material. Helpers that are transparent
 * or shader-driven — the floor grid, the ground shadow quad — therefore turn
 * into solid surfaces that only the AO pass sees, and their edges cut visible
 * bands across the image. Objects on this layer stay in the normal render (the
 * scene camera enables the layer) but the AO camera does not see them.
 *
 * `allowOverride = false` does not work here: it only skips the material swap,
 * so the helper still draws — with its own shader — into the AO G-buffer.
 */
export const AO_EXCLUDED_LAYER = 1;

/**
 * Lets the scene camera see {@link AO_EXCLUDED_LAYER}.
 *
 * Helpers on that layer are on it *exclusively* — that is what hides them from
 * the AO camera — so without this they are invisible to the scene camera too.
 * It belongs to the stage rather than to the post-processing component, which
 * unmounts when ambient occlusion is switched off and would take the floor grid
 * with it.
 */
export const useSceneLayers = (): void => {
  const camera = useThree((state) => state.camera);

  useEffect(() => {
    camera.layers.enable(AO_EXCLUDED_LAYER);
    return () => {
      camera.layers.disable(AO_EXCLUDED_LAYER);
    };
  }, [camera]);
};

/** Puts an object and its meshes on {@link AO_EXCLUDED_LAYER}. */
export const excludeFromAmbientOcclusion = (object: Object3D | null): void => {
  object?.traverse((child) => {
    // Cameras keep their own layers: a shadow camera restricted to this layer
    // would stop seeing the product it is supposed to capture.
    if ((child as Camera).isCamera) return;
    child.layers.set(AO_EXCLUDED_LAYER);
  });
};

/** Either the composed image or the AO buffer on its own. */
export type AmbientOcclusionDiagnosticView = "composed" | "ao";

export function ambientOcclusionDiagnosticView(
  showAoBuffer: boolean,
): AmbientOcclusionDiagnosticView {
  return showAoBuffer ? "ao" : "composed";
}

/**
 * What the post-processing chain needs from an ambient occlusion pass, over and
 * above `Pass` itself. {@link ProductViewerGTAOPass} is the only implementation;
 * the interface is kept because the pipeline, the tuning panel and the
 * diagnostics buffers all talk to the pass through it.
 */
export interface AmbientOcclusionPass extends Pass {
  setResolutionScale(scale: number): void;
  setDenoiseSamples(samples: 8 | 16): void;
  setDiagnosticView(view: AmbientOcclusionDiagnosticView): void;
  applyTuning(tuning: AmbientOcclusionTuning): void;
}

interface PostProcessingPipeline {
  composer: EffectComposer;
  ambientOcclusionPass: AmbientOcclusionPass;
  sized: Vector2;
  disposed: boolean;
}

/**
 * Copies what the AO G-buffer reads from the scene camera, and nothing else.
 *
 * Deliberately not `Object3D.copy(source, false)`, which this replaced. That
 * copies two things the G-buffer has no use for, on every rendered frame of
 * every drag and orbit: `animations.slice()` allocates an array, and `userData`
 * is round-tripped through `JSON.parse(JSON.stringify(...))` — a serialize and
 * a parse per frame, and a silent throw the moment anything puts a circular
 * reference or a class instance in there.
 *
 * It also sidesteps the trap that forced the non-recursive flag: `copy`
 * *appends* clones of the source's children without clearing its own, so
 * anything parented to the scene camera — a camera-mounted light, an XR
 * controller model, a drei `<PerspectiveCamera>` with children — would add a
 * fresh deep clone to `aoCamera.children` on every frame, and every one of
 * those would then be drawn into the G-buffer. Copying named properties cannot
 * touch `children` at all.
 *
 * The pose is kept exactly, deliberately — the matrix flags above all: the
 * scene camera may have `matrixWorldAutoUpdate` off and its `matrixWorld`
 * written from outside (an XR rig, a manual world matrix). Leaving that flag at
 * the clone's default would let the renderer recompose `matrixWorld` from the
 * local transform and discard the authoritative one, so the G-buffer would be
 * rendered from a different pose than the beauty pass.
 *
 * Four more of `copy`'s assignments are left out, and each is checked rather
 * than overlooked: `layers.mask`, which the caller writes itself two lines
 * below; `rotation.order`, which only names the Euler decomposition of the
 * quaternion the matrix is composed from; `matrixWorldNeedsUpdate`, which is
 * read only inside `updateMatrixWorld` and whose default of `false` is what
 * keeps a manually written `matrixWorld` from being recomposed; and
 * `coordinateSystem`, which the clone inherits at construction and which
 * nothing here changes afterwards. `view` is cloned rather than shared, as
 * `PerspectiveCamera.copy` does, so a view offset set on the AO camera cannot
 * reach into the camera the beauty pass renders through.
 */
function syncAmbientOcclusionCamera(
  target: PerspectiveCamera,
  source: PerspectiveCamera,
): void {
  target.position.copy(source.position);
  target.quaternion.copy(source.quaternion);
  target.scale.copy(source.scale);
  target.up.copy(source.up);
  target.matrix.copy(source.matrix);
  target.matrixWorld.copy(source.matrixWorld);
  target.matrixWorldInverse.copy(source.matrixWorldInverse);
  target.projectionMatrix.copy(source.projectionMatrix);
  target.projectionMatrixInverse.copy(source.projectionMatrixInverse);
  target.matrixAutoUpdate = source.matrixAutoUpdate;
  target.matrixWorldAutoUpdate = source.matrixWorldAutoUpdate;
  target.visible = source.visible;
  target.frustumCulled = source.frustumCulled;
  target.renderOrder = source.renderOrder;
  target.castShadow = source.castShadow;
  target.receiveShadow = source.receiveShadow;
  target.name = source.name;
  target.fov = source.fov;
  target.aspect = source.aspect;
  target.near = source.near;
  target.far = source.far;
  target.zoom = source.zoom;
  target.focus = source.focus;
  target.filmGauge = source.filmGauge;
  target.filmOffset = source.filmOffset;
  if (source.view === null) target.view = null;
  else if (target.view === null || target.view === source.view) {
    // Own object from here on, so the frames below cost no allocation.
    target.view = { ...source.view };
  } else Object.assign(target.view, source.view);
}

/**
 * Post-processing for the product scenes.
 *
 * Anti-aliasing uses a dedicated 4x MSAA beauty target, resolved once before
 * ambient occlusion, and SMAA on top of it. Fullscreen passes use single-sample
 * HDR buffers.
 *
 * The two are not redundant. MSAA resolves geometry coverage but shades once
 * per pixel, so it cannot touch aliasing that comes from the shading itself —
 * and on aluminium (`metalness: 0.9`, `roughness: 0.22`) the specular lobe is
 * tight enough that a groove, whether extruded or normal-mapped, breaks into a
 * staircase inside the silhouette where MSAA has nothing to average. SMAA works
 * on the resolved image, so it catches those. It costs three fullscreen draws.
 *
 * Once rendering goes through a composer the canvas's own `antialias: true`
 * is inert, because the only thing drawn to the default framebuffer is a
 * full-screen quad with no interior edges to sample.
 *
 * Image-based lighting arrives from every direction at once, so nothing in the
 * scene can shade itself: a lid seam or a corner recess receives as much light
 * as an open surface. AO puts that contact shading back.
 *
 * Rendering moves through an EffectComposer, which has two consequences worth
 * knowing:
 *
 * - The beauty target retains geometry MSAA without multisampling or resolving
 *   the composer's post-processing buffers.
 * - Tone mapping is applied by `OutputPass`, not by the material shaders: three
 *   only tone maps when it renders straight to the canvas. `OutputPass` reads
 *   `renderer.toneMapping`, so {@link ProductViewerToneMapping} still governs
 *   and the image is mapped exactly once.
 *
 * `useFrame` at priority 1 takes the frame over from R3F's automatic render.
 * Lower-priority callbacks — the ground shadow's depth pass — still run first.
 *
 * Every buffer in the chain is sized from the renderer's drawing buffer, in
 * device pixels, and re-checked each frame. The composer's own bookkeeping is
 * bypassed (`setPixelRatio(1)`) because it caches the pixel ratio it was built
 * with, while R3F reassigns `gl.setPixelRatio()` whenever its `dpr` changes —
 * a stale ratio there sizes the AO buffers differently from the canvas, which
 * shows up as a band of the frame with no occlusion in it and a stationary
 * screen-space edge. The capture canvases, which call `gl.setSize(w, h, false)`
 * and so decouple the drawing buffer from the element size entirely, are
 * covered by the same approach.
 */
export const ProductViewerPostProcessing = ({
  diagnostics = false,
  antialiasing = true,
  aoDenoiseSamples = 16,
  interactionActive,
  aoResolutionScale = 1,
}: {
  /** See `ambientOcclusionDiagnostics` on {@link ProductViewerStage}. */
  diagnostics?: boolean;
  /**
   * SMAA on top of the always-on scene MSAA, for the shading aliasing MSAA
   * cannot reach. Off only to measure what it costs or what it is hiding.
   * @default true
   */
  antialiasing?: boolean;
  /**
   * Denoise only: independent of the AO probe count. It does not drop while
   * the camera moves — GTAO's sparse spatial filter must not lose half its
   * taps during orbit, and motion is already budgeted by DPR and shadow
   * refresh frequency.
   */
  aoDenoiseSamples?: 8 | 16;
  /** Overrides automatic camera-motion sensing when an interaction AO scale is requested. */
  interactionActive?: boolean;
  /** Optional interaction-only AO scale, clamped to [0.5, 1]; idle stays full resolution. */
  aoResolutionScale?: number;
}) => {
  const gl = useThree((state) => state.gl);
  const scene = useThree((state) => state.scene);
  const camera = useThree((state) => state.camera);
  const invalidate = useThree((state) => state.invalidate);
  const store = useContext(ProductStoreContext);
  // Null outside diagnostics, so that dragging a slider does not re-render the
  // capture canvases that will never read it.
  const tuning = useAmbientOcclusionTuning((state) =>
    diagnostics ? state.tuning : null,
  );
  const cameraActivity = useMemo(
    () => createPostProcessingCameraActivity(camera, invalidate),
    [camera, invalidate],
  );
  const interactionResolutionScale =
    clampAmbientOcclusionResolutionScale(aoResolutionScale);
  // Motion sensing is only needed for automatically selected AO quality, and
  // resolution is the only dial that still drops during interaction.
  const trackCameraMotion =
    interactionActive === undefined && interactionResolutionScale < 1;

  // Mirrors the scene camera but cannot see AO_EXCLUDED_LAYER, so scene helpers
  // stay out of the G-buffer. Kept in sync with the real camera every frame.
  const aoCamera = useMemo(() => {
    const clone = (camera as PerspectiveCamera).clone();
    clone.layers.mask = camera.layers.mask;
    clone.layers.disable(AO_EXCLUDED_LAYER);
    return clone;
  }, [camera]);

  const [pipeline, setPipeline] = useState<PostProcessingPipeline | null>(null);
  useLayoutEffect(() => {
    // Native resources belong to a committed effect, not a memoized render.
    // StrictMode replays setup/cleanup; each setup needs a fresh live pipeline
    // rather than reusing passes disposed by the simulated unmount.
    const buffer = gl.getDrawingBufferSize(new Vector2());
    const target = new WebGLRenderTarget(buffer.width, buffer.height, {
      type: HalfFloatType,
      depthBuffer: false,
    });

    const instance = new EffectComposer(gl, target);
    // Sizes handed to the composer are device pixels, not CSS pixels.
    instance.setPixelRatio(1);

    const beauty = new ProductViewerBeautyPass(
      scene,
      camera,
      buffer.width,
      buffer.height,
    );
    instance.addPass(beauty);
    const ambientOcclusion: AmbientOcclusionPass = buildAmbientOcclusionPass(
      scene,
      aoCamera,
      buffer,
      beauty.target,
    );
    instance.addPass(ambientOcclusion);

    // Three's SMAA expects linear-sRGB input, before OutputPass tone mapping.
    if (antialiasing) instance.addPass(new SMAAPass());
    instance.addPass(new ProductViewerOutputPass());

    const acquired: PostProcessingPipeline = {
      composer: instance,
      ambientOcclusionPass: ambientOcclusion,
      sized: new Vector2(),
      disposed: false,
    };
    setPipeline(acquired);
    invalidate();
    return () => {
      acquired.disposed = true;
      // EffectComposer.dispose() only owns its ping-pong targets/copy pass.
      for (const pass of instance.passes) pass.dispose?.();
      instance.dispose();
    };
  }, [gl, scene, camera, aoCamera, antialiasing, invalidate]);

  useLayoutEffect(() => {
    if (!pipeline || pipeline.disposed) return;
    pipeline.ambientOcclusionPass.setDenoiseSamples(aoDenoiseSamples);
    invalidate();
  }, [pipeline, aoDenoiseSamples, invalidate]);

  useLayoutEffect(() => {
    if (!pipeline || pipeline.disposed || !tuning) return;
    pipeline.ambientOcclusionPass.applyTuning(tuning);
    invalidate();
  }, [pipeline, tuning, invalidate]);

  useLayoutEffect(() => {
    if (!pipeline || pipeline.disposed) return;
    cameraActivity.reset();
    pipeline.ambientOcclusionPass.setResolutionScale(
      interactionActive === true ? interactionResolutionScale : 1,
    );
    invalidate();
    return () => cameraActivity.reset();
  }, [
    pipeline,
    cameraActivity,
    interactionActive,
    interactionResolutionScale,
    invalidate,
  ]);

  const drawingBuffer = useMemo(() => new Vector2(), []);

  useFrame((_, delta) => {
    if (!pipeline || pipeline.disposed) return;
    const { composer, ambientOcclusionPass, sized } = pipeline;
    const cameraMoved =
      trackCameraMotion &&
      cameraActivity.update(store?.getState().isUserControllingCamera ?? false);
    const active =
      interactionActive ??
      (interactionResolutionScale < 1 &&
        (cameraMoved || isSceneDragActive(scene)));
    // What is copied, and why not `Object3D.copy`:
    // {@link syncAmbientOcclusionCamera}.
    if (!trackCameraMotion) {
      // Keep the tracker's matrix synchronization, including ancestors,
      // without comparing motion or scheduling an unused rest frame.
      camera.parent?.updateWorldMatrix(true, false);
      camera.updateWorldMatrix(false, false);
    }
    syncAmbientOcclusionCamera(aoCamera, camera as PerspectiveCamera);
    aoCamera.layers.mask = camera.layers.mask;
    aoCamera.layers.disable(AO_EXCLUDED_LAYER);

    // Track the drawing buffer itself rather than the element size: it is the
    // one number that is always exactly what the canvas renders at.
    gl.getDrawingBufferSize(drawingBuffer);
    if (!sized.equals(drawingBuffer)) {
      sized.copy(drawingBuffer);
      composer.setSize(drawingBuffer.x, drawingBuffer.y);
    }
    ambientOcclusionPass.setResolutionScale(
      active ? interactionResolutionScale : 1,
    );
    ambientOcclusionPass.setDenoiseSamples(aoDenoiseSamples);

    composer.render(delta);
  }, 1);

  // Opt-in, and only where there is a store to read it from. The preview and
  // thumbnail canvases render this stage under the *same* product store as the
  // viewer, so an unconditional read would capture the diagnostic view and
  // store it as the product image; the mesh editor has no provider at all.
  return diagnostics && store && pipeline && !pipeline.disposed ? (
    <DiagnosticBufferOutput
      store={store}
      pass={pipeline.ambientOcclusionPass}
    />
  ) : null;
};

/**
 * Switches the pass between the composed image and the AO buffer on its own.
 *
 * Ambient occlusion on a case is a handful of narrow creases — the shell is
 * convex, so there is very little for it to find — and those creases land on
 * specular aluminium, where a tone-mapped highlight swallows them. Judging a
 * change to the AO through the finished image is guesswork; this shows what
 * the pass actually produced.
 */
const DiagnosticBufferOutput = ({
  store,
  pass,
}: {
  store: AnyProductStoreHook;
  pass: AmbientOcclusionPass;
}) => {
  const showAo = store((state) => state.showAmbientOcclusionBuffer);

  useEffect(() => {
    pass.setDiagnosticView(ambientOcclusionDiagnosticView(showAo));
    return () => {
      pass.setDiagnosticView("composed");
    };
  }, [pass, showAo]);

  return null;
};

function buildAmbientOcclusionPass(
  scene: Scene,
  camera: Camera,
  buffer: Vector2,
  beautyTarget: WebGLRenderTarget,
): ProductViewerGTAOPass {
  const gtao = new ProductViewerGTAOPass(
    scene,
    camera,
    beautyTarget,
    buffer.width,
    buffer.height,
  );
  gtao.updateGtaoMaterial({
    radius: AMBIENT_OCCLUSION.radius,
    scale: AMBIENT_OCCLUSION.scale,
    thickness: AMBIENT_OCCLUSION.thickness,
    distanceExponent: AMBIENT_OCCLUSION.distanceExponent,
    samples: AMBIENT_OCCLUSION.samples,
    screenSpaceRadius: false,
  });

  // {@link AmbientOcclusionBufferOutput} renders the AO buffer on its own. If
  // an artefact is visible there it comes from the G-buffer or AO sizing; if
  // that buffer is clean and only the composited image shows it, the blend or
  // output stage is at fault.
  gtao.blendIntensity = AMBIENT_OCCLUSION.intensity;
  return gtao;
}
